import { v4 as uuidv4 } from 'uuid';
import { track, flush as flushAnalytics, EVENTS } from '../analytics';

// Observer only: slow events never abort capture or retry metered operations.
// `reason` is the SDK/ingest-supported correlation field for this event only.
// It contains a fresh random attempt ID, never a consultation/session/user ID.
export function createLiveStartupDiagnostics({ type = 'in_person', emit = track, flush = flushAnalytics,
  now = () => performance.now(), slowMs = 10000 } = {}) {
  const reason = `attempt:${uuidv4()}`;
  const started = now();
  const pending = new Map();
  let ended = false;
  let failed = false;
  const flushPending = () => {
    // Never await telemetry on the microphone/session path or leak a rejection.
    try { Promise.resolve(flush?.()).catch(() => {}); } catch (_) {}
  };
  const send = (stage, step, since, errorCode) => {
    try {
      emit(EVENTS.LIVE_STARTUP_DIAGNOSTIC, { reason, type, stage, step,
        duration_ms: Math.max(0, Math.round(now() - since)),
        dwell_ms: Math.max(0, Math.round(now() - started)),
        ...(errorCode ? { error_code: errorCode } : {}) });
    } catch (_) { /* Telemetry must never change recording behavior. */ }
  };
  const begin = (stage) => {
    if (ended || pending.has(stage)) return;
    const since = now();
    const timer = setTimeout(() => {
      if (!ended) { send(stage, 'still_waiting', since); flushPending(); }
    }, slowMs);
    pending.set(stage, { since, timer });
    send(stage, 'started', since);
  };
  const end = (stage, step, errorCode) => {
    const entry = pending.get(stage);
    if (ended || !entry) return;
    clearTimeout(entry.timer);
    pending.delete(stage);
    if (step === 'failed' || step === 'timeout') failed = true;
    send(stage, step, entry.since, errorCode);
  };
  const finish = (outcome) => {
    if (ended) return;
    for (const [stage, entry] of pending) {
      clearTimeout(entry.timer);
      if (stage !== 'startup') send(stage, 'cancelled', entry.since);
    }
    pending.clear();
    send('startup', outcome || (failed ? 'failed' : 'cancelled'), started);
    ended = true;
    flushPending();
  };
  const fail = (stage, error) => end(stage, 'failed',
    ['NotAllowedError', 'NotFoundError', 'NotReadableError', 'AbortError', 'TimeoutError'].includes(error?.name)
      ? error.name : 'operation_failed');
  const run = async (stage, task, valid = () => true, failureCode = () => undefined) => {
    begin(stage);
    try {
      const value = await task();
      const succeeded = valid(value);
      end(stage, succeeded ? 'completed' : 'failed', succeeded ? undefined : failureCode(value));
      return value;
    } catch (error) {
      end(stage, error?.name === 'TimeoutError' ? 'timeout' : 'failed',
        ['NotAllowedError', 'NotFoundError', 'NotReadableError', 'AbortError', 'TimeoutError'].includes(error?.name)
          ? error.name : 'operation_failed');
      throw error;
    }
  };
  begin('startup');
  // Overall duration is emitted once by finish; keep its watchdog only.
  return { run, begin, end, finish, fail };
}
